const LEARNING_PERFORMANCE_TRACE_QUERY = 'performanceTrace'
const LEARNING_PERFORMANCE_TRACE_VALUES = new Set([
  'learner-entry',
  'flashcards',
  'learning-query',
  'online-ebook-list',
])

type LearningPerformanceTraceValue = string | number | boolean | null | undefined

export type LearningPerformanceTraceDetails = Readonly<
  Record<string, LearningPerformanceTraceValue>
>

function isBrowserPerformanceAvailable() {
  return typeof window !== 'undefined' && typeof performance !== 'undefined'
}

export function isLearningPerformanceTraceEnabled() {
  if (typeof window === 'undefined') {
    return false
  }

  const traceValue = new URLSearchParams(window.location.search).get(
    LEARNING_PERFORMANCE_TRACE_QUERY,
  )

  return traceValue !== null && LEARNING_PERFORMANCE_TRACE_VALUES.has(traceValue)
}

export function getLearningPerformanceTimestamp() {
  return isLearningPerformanceTraceEnabled() && isBrowserPerformanceAvailable()
    ? performance.now()
    : null
}

function normalizeTraceDetails(details: LearningPerformanceTraceDetails = {}) {
  return Object.fromEntries(
    Object.entries(details).filter(([, value]) => value !== undefined),
  )
}

export function logLearningPerformanceTrace(
  event: string,
  details: LearningPerformanceTraceDetails = {},
) {
  if (!isLearningPerformanceTraceEnabled()) {
    return
  }

  const atMs = isBrowserPerformanceAvailable() ? Math.round(performance.now()) : null

  console.info(
    '[lms-learning-performance]',
    JSON.stringify({
      atMs,
      event,
      ...normalizeTraceDetails(details),
    }),
  )
}

export async function traceLearningPerformance<T>({
  event,
  getDetails,
  task,
}: {
  readonly event: string
  readonly getDetails?: (result: T) => LearningPerformanceTraceDetails
  readonly task: () => Promise<T>
}): Promise<T> {
  if (!isLearningPerformanceTraceEnabled() || !isBrowserPerformanceAvailable()) {
    return task()
  }

  const startedAt = performance.now()

  try {
    const result = await task()

    logLearningPerformanceTrace(event, {
      durationMs: Math.round(performance.now() - startedAt),
      status: 'success',
      ...getDetails?.(result),
    })

    return result
  } catch (error) {
    logLearningPerformanceTrace(event, {
      durationMs: Math.round(performance.now() - startedAt),
      errorName: error instanceof Error ? error.name : 'unknown',
      status: 'error',
    })

    throw error
  }
}
